import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Loader } from "@progress/kendo-react-indicators";
import isEmpty from "lodash-es/isEmpty";
import { AlertTriangle as AlertIcon, Info as InfoIcon } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { If, Then } from "react-if";
import {
  DataPlaneNotificationControllerService,
  DataPlaneNotificationDashboardResponseModel,
} from "@/api";
import { getImage } from "@/components/Kendo/Helpers/DataSourceRenderItem";
import MultiSelect from "@/components/MultiSelect";
import { useAccounts } from "@/context/UserContext";
import useQuery from "@/hooks/useQuery";
import NoAlertIcon from "@/icons/no_alert.svg?react";
import { getDateDistance } from "@/libs/date";
import { IAccount } from "@/types/api";

// Lazy-loaded: pulls in react-markdown + sql-formatter/prismjs, which should
// only ship to users who actually expand a remediation item.
const MarkDown = React.lazy(() => import("@/components/Markdown"));

export enum NotificationType {
  ALERT = "ALERT",
  DELTA_SYNC_SERVICE = "DELTA_SYNC_SERVICE",
}

export type StatusValue = "FAILED" | "COMPLETED";

export const STATUS_VALUE: Record<NotificationType, StatusValue> = {
  ALERT: "FAILED",
  DELTA_SYNC_SERVICE: "COMPLETED",
};

export type SelectData = {
  id: NotificationType;
  name: string;
};

export interface AlertsProps {
  markAsRead?: (id: string) => void;
}

export const Item: React.FC<{
  item: DataPlaneNotificationDashboardResponseModel;
  type: NotificationType;
}> = ({ item, type }) => {
  const [show, setShow] = React.useState<boolean>(false);
  const account = useAccounts().find(account => account.id === item.accountId);

  const onClick = useCallback(() => {
    setShow(!show);
  }, [show]);

  return (
    <div className="tlx-health-menu--popup-content-item">
      <div className="tlx-health-menu--popup-item-content">
        <div className={`tlx-health-menu--popup-item-icon icon-${type}`}>
          {type === NotificationType.ALERT ? (
            <AlertIcon color="var(--tlx-status-error-text)" />
          ) : (
            <InfoIcon color="var(--tlx-status-info-text)" />
          )}
        </div>
        <div className="tlx-health-menu--popup-item-body">
          <If condition={!isEmpty(account)}>
            <Then>
              <div className="datasource">
                {account && getImage(account)}
                <div>{account?.name}</div>
              </div>
            </Then>
          </If>
          <div className="body">
            <div className="title">{item.issue}</div>
            <If condition={!!item.remedy}>
              <Then>
                <div className="tlx-health-menu--popup-item-body-icon-expand arrow-chevron-icon">
                  <Button
                    size="medium"
                    fillMode="flat"
                    className="tlx-collapse-container--button"
                    onClick={onClick}
                    icon={`arrow-chevron-${show ? "up" : "down"}`}
                  />
                </div>
              </Then>
            </If>
          </div>
          <If condition={show}>
            <Then>
              <div className="remedy">
                <strong>Remediation:</strong>{" "}
                <React.Suspense fallback={item.remedy}>
                  <MarkDown>{item.remedy}</MarkDown>
                </React.Suspense>
              </div>
            </Then>
          </If>
          <div className="date">{getDateDistance(item.createdAt!)}</div>
        </div>
      </div>
    </div>
  );
};

export const Items: React.FC<{
  accountId: string[];
  type: NotificationType;
  markAsRead?: (id: string) => void;
}> = ({ accountId, type, markAsRead }) => {
  const { data = [], isLoading } = useQuery({
    method:
      DataPlaneNotificationControllerService.listDataPlaneNotificationForDashboardUsingGet,
    params: {
      accountId,
      pageNo: 1,
      pageSize: 1000,
      sinceDays: 7,
      sortBy: "createdDate",
      sortOrder: "DESC",
      statusValue: STATUS_VALUE[type],
      type,
    },
  });

  // Mark items as read when they are displayed
  useEffect(() => {
    if (markAsRead && data.length > 0) {
      data.forEach(item => {
        if (item.id) {
          markAsRead(item.id);
        }
      });
    }
  }, [data, markAsRead]);

  return (
    <div className="tlx-health-menu--popup-content">
      {isLoading && <Loader type="converging-spinner" />}
      {data.map(item => (
        <Item item={item} key={item.id} type={type} />
      ))}
      {!isLoading && data.length === 0 && (
        <div className="tlx-health-menu--popup-content-empty">
          <NoAlertIcon />
          No Alerts Found
        </div>
      )}
    </div>
  );
};

function Alerts({ markAsRead }: AlertsProps = {}): React.ReactElement {
  const accounts = useAccounts();
  const [selectAccounts, setSelectAccounts] = useState<IAccount[]>([]);
  const typesData = useMemo(
    () => [
      { id: NotificationType.ALERT, name: "Alerts" },
      { id: NotificationType.DELTA_SYNC_SERVICE, name: "Updates" },
    ],
    [],
  );
  const [type, setType] = useState<SelectData>(typesData[0]);
  const { data = [], isLoading } = useQuery({
    method:
      DataPlaneNotificationControllerService.listDataPlaneNotificationUsingGet1,
    params: {
      type: type.id,
    },
  });

  const filterAccounts = accounts.filter(account => data.includes(account.id!));

  return (
    <div className="tlx-health-menu--container">
      <div className="tlx-health-menu--container-header">
        <div className="dataSources">
          <MultiSelect
            data={filterAccounts}
            placeholder="Data Sources"
            name="dataSource"
            filterable={true}
            imageRender={getImage}
            value={selectAccounts}
            loading={isLoading}
            onChange={({ value }) => {
              setSelectAccounts(value);
            }}
            valid={true}
          />
        </div>
        <div className="types">
          <DropDownList
            data={typesData}
            textField="name"
            dataItemKey="id"
            name="type"
            value={type}
            onChange={({ value }) => {
              setType(value);
              setSelectAccounts([]);
            }}
          />
        </div>
      </div>
      <Items
        accountId={selectAccounts.map(item => item.id) as string[]}
        type={type.id}
        markAsRead={markAsRead}
      />
    </div>
  );
}

export default Alerts;
